import { 
  Message, 
  SendSMSRequest, 
  SendSMSResponse, 
  MessageStats, 
  PaginatedResponse, 
  Contact, 
  ApiError 
} from '../types';

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData: ApiError = await response.json().catch(() => ({}));
      const errorMessage = errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    const finalOptions = { ...defaultOptions, ...options };

    try {
      const response = await fetch(url, finalOptions);
      return await this.handleResponse<T>(response);
    } catch (error) {
      console.error(`API Error [${endpoint}]:`, error);
      throw error;
    }
  }

  // Messages API
  async getMessages(params?: {
    page?: number;
    direction?: 'inbound' | 'outbound';
    status?: string;
    phone?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<PaginatedResponse<Message>> {
    const searchParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
    }

    const queryString = searchParams.toString();
    const endpoint = `/api/messaging/messages/${queryString ? `?${queryString}` : ''}`;
    
    return this.makeRequest<PaginatedResponse<Message>>(endpoint);
  }

  async getMessage(id: string): Promise<Message> {
    return this.makeRequest<Message>(`/api/messaging/messages/${id}/`);
  }

  async sendSMS(data: SendSMSRequest): Promise<SendSMSResponse> {
    return this.makeRequest<SendSMSResponse>('/api/messaging/messages/send_sms/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async retryMessage(id: string): Promise<SendSMSResponse> {
    return this.makeRequest<SendSMSResponse>(`/api/messaging/messages/${id}/retry/`, {
      method: 'POST',
    });
  }

  async getMessageStats(): Promise<{ success: boolean; data: MessageStats }> {
    return this.makeRequest<{ success: boolean; data: MessageStats }>('/api/messaging/messages/stats/');
  }

  // Contacts API
  async getContacts(params?: {
    page?: number;
    is_active?: boolean;
    opted_out?: boolean;
    search?: string;
  }): Promise<PaginatedResponse<Contact>> {
    const searchParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
    }

    const queryString = searchParams.toString();
    const endpoint = `/api/messaging/contacts/${queryString ? `?${queryString}` : ''}`;
    
    return this.makeRequest<PaginatedResponse<Contact>>(endpoint);
  }

  async getContact(id: string): Promise<Contact> {
    return this.makeRequest<Contact>(`/api/messaging/contacts/${id}/`);
  }

  async updateContact(id: string, data: Partial<Contact>): Promise<Contact> {
    return this.makeRequest<Contact>(`/api/messaging/contacts/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async optOutContact(id: string): Promise<{ success: boolean; message: string }> {
    return this.makeRequest<{ success: boolean; message: string }>(`/api/messaging/contacts/${id}/opt_out/`, {
      method: 'POST',
    });
  }

  async optInContact(id: string): Promise<{ success: boolean; message: string }> {
    return this.makeRequest<{ success: boolean; message: string }>(`/api/messaging/contacts/${id}/opt_in/`, {
      method: 'POST',
    });
  }

  // WebSocket URL
  getWebSocketUrl(): string {
    // Get WebSocket URL from environment variable or construct from API URL
    const envWsUrl = process.env.REACT_APP_WS_URL;
    
    if (envWsUrl) {
      console.log('Using WebSocket URL from environment:', envWsUrl);
      return envWsUrl;
    }
    
    const constructedUrl = this.constructWebSocketUrl();
    console.log('Constructed WebSocket URL from API URL:', constructedUrl);
    console.warn('REACT_APP_WS_URL not set, using constructed URL. Consider setting REACT_APP_WS_URL environment variable.');
    
    return constructedUrl;
  }

  private constructWebSocketUrl(): string {
    // Fallback: construct WebSocket URL from API URL
    const apiUrl = this.baseUrl;
    
    // Convert HTTP/HTTPS to WS/WSS
    let wsUrl = apiUrl.replace(/^https?/, (match) => match === 'https' ? 'wss' : 'ws');
    
    // Add WebSocket path if not already present
    if (!wsUrl.includes('/ws/messages/')) {
      wsUrl = wsUrl.endsWith('/') ? wsUrl : wsUrl + '/';
      wsUrl += 'ws/messages/';
    }
    
    return wsUrl;
  }

  // Health check
  async healthCheck(): Promise<{ status: string }> {
    return this.makeRequest<{ status: string }>('/api/health/');
  }
}

// Create singleton instance
export const apiService = new ApiService();

// Export individual methods for easier imports
export const {
  getMessages,
  getMessage,
  sendSMS,
  retryMessage,
  getMessageStats,
  getContacts,
  getContact,
  updateContact,
  optOutContact,
  optInContact,
  getWebSocketUrl,
  healthCheck,
} = apiService;